import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { generatePrompts } from '../../services/api';
import { MODELS, DEFAULT_MODEL } from '../../constants/models';
import { getDefaultLLMConfig } from '../../constants/llmConfig';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import logger from '../../utils/logger';

import StepDescription from './steps/StepDescription';
import StepUseCase from './steps/StepUseCase';
import StepStructure from './steps/StepStructure';
import StepVariables from './steps/StepVariables';
import StepOutputFormat from './steps/StepOutputFormat';
import StepSchema from './steps/StepSchema';
import StepConfig from './steps/StepConfig';
import ProgressBar from './ProgressBar';

const STEPS = [
  { id: 'description', title: 'Description', component: StepDescription, required: true },
  { id: 'useCase', title: 'Use Case', component: StepUseCase, required: true },
  { id: 'structure', title: 'Structure', component: StepStructure, required: true },
  { id: 'variables', title: 'Variables', component: StepVariables, required: false },
  { id: 'outputFormat', title: 'Output Format', component: StepOutputFormat, required: true },
  { id: 'schema', title: 'Schema', component: StepSchema, conditional: true, required: true },
  { id: 'config', title: 'Configuration', component: StepConfig, required: false },
];

const initialFormData = {
  description: '',
  use_case: '',
  type: 'System User',
  input_variables: [],
  output_type: 'Plain Text',
  schema: '',
  additional_config: {
    output_constraints: '',
    must_include: '',
    must_avoid: '',
    special_instructions: '',
  },
};

const PromptWizard = ({ projectName, projectId, workspaceId, onComplete, onStreamProjectId, onStreamChunk, onStreamComplete, onStreamError }) => {
  const { getAvailableProviders } = useWorkspace();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [llmConfig, setLLMConfig] = useState(() => getDefaultLLMConfig(DEFAULT_MODEL));
  const [errors, setErrors] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  
  const availableProviders = getAvailableProviders();

  const getVisibleSteps = useCallback(() => {
    return STEPS.filter(step => {
      if (step.id === 'schema') {
        return formData.output_type === 'Schema';
      }
      return true;
    });
  }, [formData.output_type]);

  const visibleSteps = getVisibleSteps();
  const currentStepData = visibleSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === visibleSteps.length - 1;

  const validateStep = useCallback((stepId) => {
    const newErrors = {};

    switch (stepId) {
      case 'description':
        if (!formData.description.trim()) {
          newErrors.description = 'Description is required';
        } else if (formData.description.trim().length < 10) {
          newErrors.description = 'Description must be at least 10 characters';
        }
        break;

      case 'useCase':
        if (!formData.use_case.trim()) {
          newErrors.use_case = 'Use case is required';
        } else if (formData.use_case.trim().length < 10) {
          newErrors.use_case = 'Use case must be at least 10 characters';
        }
        break;

      case 'structure':
        if (!formData.type) {
          newErrors.type = 'Please select a prompt structure';
        }
        break;

      case 'outputFormat':
        if (!formData.output_type) {
          newErrors.output_type = 'Please select an output format';
        }
        break;

      case 'schema':
        if (formData.output_type === 'Schema' && !formData.schema.trim()) {
          newErrors.schema = 'JSON schema is required when output format is Schema';
        } else if (formData.output_type === 'Schema') {
          try {
            JSON.parse(formData.schema);
          } catch (e) {
            newErrors.schema = 'Invalid JSON syntax';
          }
        }
        break;

      case 'variables':
        const incompleteVars = formData.input_variables.filter(
          v => v.name.trim() && !v.description.trim()
        );
        if (incompleteVars.length > 0) {
          newErrors.variables = 'All variables must have both name and description';
        }
        break;

      case 'config':
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const updateFormData = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const updateAdditionalConfig = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      additional_config: {
        ...prev.additional_config,
        [field]: value,
      },
    }));
  }, []);

  const handleModelChange = useCallback((newModel) => {
    setModel(newModel);
    setLLMConfig(prev => ({ ...prev, model: newModel }));
  }, []);

  const handleNext = useCallback(() => {
    if (!validateStep(currentStepData?.id)) {
      const errorMessages = Object.values(errors);
      if (errorMessages.length > 0) {
        toast.error(errorMessages[0]);
      }
      return;
    }

    if (!isLastStep) {
      setCurrentStep(prev => prev + 1);
      setErrors({});
    }
  }, [isLastStep, currentStepData, validateStep, errors]);

  const handleBack = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
      setErrors({});
    }
  }, [isFirstStep]);

  const handleGenerate = useCallback(async () => {
    if (!validateStep(currentStepData?.id)) {
      const errorMessages = Object.values(errors);
      if (errorMessages.length > 0) {
        toast.error(errorMessages[0]);
      }
      return;
    }

    setIsGenerating(true);

    const finalLLMConfig = { ...llmConfig, model };

    const payload = {
      user_input: {
        description: formData.description.trim(),
        use_case: formData.use_case.trim(),
        type: formData.type,
        output_type: formData.output_type,
        schema: formData.output_type === 'Schema' ? formData.schema.trim() : null,
        input_variables: formData.input_variables.filter(v => v.name.trim()),
        additional_config: formData.additional_config,
      },
      llm_config: finalLLMConfig,
    };

    logger.userAction('Generate prompts (streaming)', { projectName, projectId, model, output_type: formData.output_type });

    // Wrap callbacks to handle loading state
    const wrappedOnStreamChunk = (chunk) => {
      setIsGenerating(false); // Stop loader when first chunk arrives
      if (onStreamChunk) onStreamChunk(chunk);
    };

    const wrappedOnStreamError = (error) => {
      setIsGenerating(false);
      if (onStreamError) onStreamError(error);
    };

    await generatePrompts(
      projectName,
      projectId,
      payload,
      onStreamProjectId,
      wrappedOnStreamChunk,
      onStreamComplete,
      wrappedOnStreamError,
      workspaceId
    );
  }, [formData, model, llmConfig, projectName, projectId, workspaceId, currentStepData, validateStep, errors, onStreamProjectId, onStreamChunk, onStreamComplete, onStreamError]);


  const isCurrentStepValid = useCallback(() => {
    switch (currentStepData?.id) {
      case 'description':
        return formData.description.trim().length >= 10;
      case 'useCase':
        return formData.use_case.trim().length >= 10;
      case 'structure':
        return !!formData.type;
      case 'outputFormat':
        return !!formData.output_type;
      case 'schema':
        if (formData.output_type !== 'Schema') return true;
        try {
          JSON.parse(formData.schema);
          return formData.schema.trim().length > 0;
        } catch {
          return false;
        }
      default:
        return true;
    }
  }, [currentStepData, formData]);

  const StepComponent = currentStepData?.component;

  return (
    <div className="w-full h-screen flex justify-center items-center py-6 px-6 bg-bg-primary overflow-hidden">
      <div className="w-full max-w-[850px] h-[calc(100vh-48px)] max-h-[900px] bg-bg-secondary rounded-xl border border-border-primary shadow-2xl overflow-hidden flex flex-col animate-fadeIn">
        <div className="flex items-center gap-2 py-4 px-6 bg-gradient-to-b from-bg-tertiary to-bg-secondary border-b border-border-primary flex-shrink-0">
          <span className="text-[10px] font-semibold text-text-tertiary uppercase tracking-wider">Project:</span>
          <span className="text-base font-semibold text-text-primary font-mono">{projectName}</span>
        </div>
        
        <ProgressBar 
          steps={visibleSteps} 
          currentStep={currentStep} 
        />

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden p-0 m-0">
          <div className="flex-1 py-6 px-6 overflow-y-auto overflow-x-hidden min-h-0 [&::-webkit-scrollbar]:w-[10px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-track]:my-2 [&::-webkit-scrollbar-thumb]:bg-border-primary [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-bg-secondary [&::-webkit-scrollbar-thumb:hover]:bg-text-tertiary">
            {StepComponent && (
              <StepComponent
                formData={formData}
                updateFormData={updateFormData}
                updateAdditionalConfig={updateAdditionalConfig}
                model={model}
                setModel={handleModelChange}
                llmConfig={llmConfig}
                setLLMConfig={setLLMConfig}
                models={MODELS}
                errors={errors}
                workspaceId={workspaceId}
                projectId={projectId}
                projectName={projectName}
                availableProviders={availableProviders}
              />
            )}
          </div>

          <div className="flex justify-between items-center py-4 px-6 border-t border-border-primary bg-bg-secondary flex-shrink-0">
            <button
              type="button"
              onClick={handleBack}
              className="py-2 px-4 text-sm font-medium bg-bg-tertiary text-text-primary border border-border-primary rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-bg-hover hover:border-border-hover disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isFirstStep}
            >
              Back
            </button>

            {isLastStep ? (
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating}
                className="py-2 px-4 text-sm font-medium bg-primary-600 text-white border border-primary-600 rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-primary-500 hover:border-primary-500 hover:-translate-y-px hover:shadow-md active:bg-primary-700 active:border-primary-700 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  'Generate Prompt'
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                className="py-2 px-4 text-sm font-medium bg-primary-600 text-white border border-primary-600 rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-primary-500 hover:border-primary-500 hover:-translate-y-px hover:shadow-md active:bg-primary-700 active:border-primary-700 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!isCurrentStepValid()}
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

PromptWizard.propTypes = {
  projectName: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  workspaceId: PropTypes.string,
  onComplete: PropTypes.func,
  onStreamProjectId: PropTypes.func,
  onStreamChunk: PropTypes.func,
  onStreamComplete: PropTypes.func,
  onStreamError: PropTypes.func,
};

PromptWizard.defaultProps = {
  workspaceId: null,
  onComplete: null,
  onStreamProjectId: null,
  onStreamChunk: null,
  onStreamComplete: null,
  onStreamError: null,
};

export default PromptWizard;
